import { useCallback, useContext, useId, useMemo, useState } from 'react'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Heading from 'LuxKit/Typography/Heading'
import Label from 'LuxKit/Label/Label'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import TextLink from 'LuxKit/TextLink'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineCameraIcon from 'LuxKit/Icons/line/LineCameraIcon'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineMinusIcon from 'LuxKit/Icons/line/LineMinusIcon'
import SolidBedIcon from 'LuxKit/Icons/solid/SolidBedIcon'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import ModalContext from 'contexts/ModalContext'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import useListTruncator from 'hooks/useListTruncator'
import type {
  HotelRoomOptionSelection,
  HotelRoomOptionsResult,
  TripItineraryData,
} from 'tripPlanner/api/types'
import ChatHotelRoomOptionsRateOption, { PriceActionsColumn, RateRow } from './ChatHotelRoomOptionsRateOption'
import ChatHotelRoomAmenitiesModal from './ChatHotelRoomAmenitiesModal'
import {
  DEFAULT_VISIBLE_RATE_COUNT,
  ROOM_CARD_HORIZONTAL_BREAKPOINT,
} from './chatHotelRoomOptionsLayout'

type RoomOption = HotelRoomOptionsResult['roomOptions'][number]
type RoomRate = RoomOption['rates'][number]

const RoomCard = styled.div`
  container-type: inline-size;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  overflow: hidden;
`

const RoomSummary = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  &.has-image {
    grid-template-areas: "image" "details";

    @container (min-width: ${rem(ROOM_CARD_HORIZONTAL_BREAKPOINT)}) {
      grid-template-areas: "image details";
      grid-template-columns: clamp(${rem(220)}, 42%, ${rem(285)}) minmax(0, 1fr);
      min-height: ${rem(180)};
    }
  }
`

const RoomImageFrame = styled.div`
  grid-area: image;
  position: relative;
  height: ${rem(180)};
  background-color: ${props => props.theme.palette.neutral.default.six};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  /* Rebrand has no mid-tone neutral; Light Surface ALT is the nearest fill. */
  body.${REBRAND_PALETTE_CLASS} & {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
  }

  @container (min-width: ${rem(ROOM_CARD_HORIZONTAL_BREAKPOINT)}) {
    height: auto;
  }
`

const RoomImageCarousel = styled(ImageCarousel)`
  height: 100%;
  width: 100%;
`

const PhotoCountBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};
  padding: ${rem(4)} ${rem(8)};
  border-radius: ${rem(4)};
  background-color: ${props => props.theme.palette.neutral.control.black32};
  color: ${props => props.theme.palette.neutral.surface.staticWhite};
`

const RoomDetails = styled.div`
  grid-area: details;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(12)};
  padding: ${rem(16)};

  @container (min-width: ${rem(ROOM_CARD_HORIZONTAL_BREAKPOINT)}) {
    padding: ${rem(20)};
  }
`

const RateList = styled.div`
  display: flex;
  flex-direction: column;

  > * + * {
    border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const CollapsedRateList = styled(RateList)`
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const MorePackagesToggle = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  padding: ${rem(16)};
  border: 0;
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: none;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  cursor: pointer;
  text-align: start;

  &:hover {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  }

  @container (min-width: ${rem(ROOM_CARD_HORIZONTAL_BREAKPOINT)}) {
    padding: ${rem(16)} ${rem(24)};
  }
`

const ROOM_IMAGE_PARAMS = {
  width: 760,
  height: 428,
  fit: 'center' as const,
  gravity: 'auto' as const,
  objectFit: 'cover' as const,
}

function isRoomOnlySelection(selection: HotelRoomOptionSelection): boolean {
  return !!selection.roomTypeId && !selection.packageId && !selection.roomRateId
}

function isRateSelected(
  rate: RoomRate,
  selections: Array<HotelRoomOptionSelection>,
): boolean {
  return selections.some((selection) =>
    (!!selection.packageId && selection.packageId === rate.packageId) ||
    (!!selection.roomRateId && selection.roomRateId === rate.roomRateId),
  )
}

/**
 * Packages the assistant explicitly picked lead the list, so the row carrying
 * the "Selected" action is always one of the ones shown before the
 * "View N more packages" row. A room-only selection targets no package, so it
 * leaves the backend's own ordering alone.
 */
function orderRatesBySelection(
  rates: Array<RoomRate>,
  selections: Array<HotelRoomOptionSelection>,
): Array<RoomRate> {
  if (!selections.some((selection) => !isRoomOnlySelection(selection))) return rates

  const selected = rates.filter((rate) => isRateSelected(rate, selections))
  if (selected.length === 0 || selected.length === rates.length) return rates

  return [...selected, ...rates.filter((rate) => !isRateSelected(rate, selections))]
}

function RoomImageGallery({ imageIds }: { imageIds: Array<string> }) {
  const images = useMemo(() => imageIds.map((id) => ({ id })), [imageIds])

  if (images.length === 0) {
    return <SolidBedIcon size="L" colour="neutral-three" />
  }

  return (
    <RoomImageCarousel
      images={images}
      imageParams={ROOM_IMAGE_PARAMS}
      arrowBehaviour={images.length > 1 ? 'interaction-visible' : 'hidden'}
      disablePlaceholders
      /* The design carries a single photo-count badge, so occupy the bottom-left
         slot to suppress the carousel's own "1 / 5" position counter. */
      bottomLeft={<span aria-hidden />}
      bottomRight={images.length > 1 && (
        <PhotoCountBadge>
          <LineCameraIcon size="XS" />
          <BodyText variant="small" weight="bold">{images.length}</BodyText>
        </PhotoCountBadge>
      )}
    />
  )
}

interface Props {
  room: RoomOption
  result: HotelRoomOptionsResult
  selections: Array<HotelRoomOptionSelection>
  pricingUnavailableMessage: string
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
}

function ChatHotelRoomOptionsRoomCard({
  room,
  result,
  selections,
  pricingUnavailableMessage,
  itineraryData,
  onSendMessage,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const morePackagesId = useId()
  const [morePackagesOpen, setMorePackagesOpen] = useState(false)
  const toggleMorePackages = useCallback(() => setMorePackagesOpen((open) => !open), [])

  const orderedRates = useMemo(
    () => orderRatesBySelection(room.rates, selections),
    [room.rates, selections],
  )
  const [visibleRates, collapsedRates] = useListTruncator(DEFAULT_VISIBLE_RATE_COUNT, orderedRates)

  const showAmenitiesModal = useCallback(() => {
    showModal(<ChatHotelRoomAmenitiesModal room={room} checkIn={result.checkIn} />)
  }, [result.checkIn, room, showModal])

  const rateRowProps = {
    room,
    result,
    itineraryData,
    onSendMessage,
    currencyCode: result.currencyCode,
    pricingMode: result.pricingMode,
    nights: result.nights,
    checkIn: result.checkIn,
  }

  const hasImages = room.imageIds.length > 0
  const hasRoomDetail = room.amenities.length > 0 || room.rates.length > 0

  return (
    <RoomCard>
      <RoomSummary className={hasImages ? 'has-image' : undefined}>
        {hasImages && (
          <RoomImageFrame data-testid="chat-room-image">
            <RoomImageGallery imageIds={room.imageIds} />
          </RoomImageFrame>
        )}
        <RoomDetails data-testid="chat-room-details">
          <Heading variant="heading5">{room.roomName}</Heading>
          {room.bedGroupDescriptions.length > 0 && (
            <LabelGroup>
              {room.bedGroupDescriptions.map((bedGroup) => (
                <Label
                  key={bedGroup}
                  kind="tertiary"
                  variant="ghost"
                  startIcon={<LineBedIcon size="XS" />}
                >
                  {bedGroup}
                </Label>
              ))}
            </LabelGroup>
          )}
          {hasRoomDetail && (
            <TextLink size="medium" weight="regular" onClick={showAmenitiesModal}>
              {intl.formatMessage({
                id: '_y3Qj7fXsuyqZc41k2DR',
                defaultMessage: 'View amenities & policies',
                description: 'Link opening the room amenities and policies modal from trip planner chat room options',
              })}
            </TextLink>
          )}
        </RoomDetails>
      </RoomSummary>

      {room.rates.length === 0 && (
        <RateRow>
          <BodyTextBlock
            variant="medium"
            weight="bold"
            startIcon={<LineInfoCircleIcon size="XS" />}
          >
            {pricingUnavailableMessage}
          </BodyTextBlock>
          <PriceActionsColumn>
            {onSendMessage && (
              <AddHotelToTripButton
                itineraryData={itineraryData}
                hotelName={result.hotelName}
                offerId={result.offerId}
                onSendMessage={onSendMessage}
                filterNights={result.nights}
                checkIn={result.checkIn}
                checkOut={result.checkOut}
                kind="primary"
                size="medium"
                fit="full-width"
              />
            )}
          </PriceActionsColumn>
        </RateRow>
      )}

      {visibleRates.length > 0 && (
        <RateList>
          {visibleRates.map((rate, index) => (
            <ChatHotelRoomOptionsRateOption
              key={`${rate.packageId ?? rate.packageName ?? 'package'}-${rate.roomRateId ?? rate.rateName ?? `rate-${index}`}`}
              rate={rate}
              {...rateRowProps}
              isRoomOptionSelected={isRateSelected(rate, selections)}
            />
          ))}
        </RateList>
      )}

      {collapsedRates.length > 0 && (
        <>
          <MorePackagesToggle
            type="button"
            aria-expanded={morePackagesOpen}
            aria-controls={morePackagesId}
            onClick={toggleMorePackages}
          >
            {morePackagesOpen ? <LineMinusIcon size="S" /> : <LinePlusIcon size="S" />}
            <BodyText variant="medium" weight="bold" colour="neutral-two">
              {morePackagesOpen
                ? intl.formatMessage({
                    id: 'SlyQIVByUrpF0VEGZf78',
                    defaultMessage: 'View fewer packages',
                    description: 'Row collapsing the additional package options for a room in trip planner chat room options',
                  })
                : intl.formatMessage(
                    {
                      id: 'mEL9MFVgzJgZRs9K_CRc',
                      defaultMessage: 'View {count, plural, one {# more package} other {# more packages}}',
                      description: 'Row expanding the additional package options for a room in trip planner chat room options',
                    },
                    { count: collapsedRates.length },
                  )}
            </BodyText>
          </MorePackagesToggle>
          {morePackagesOpen && (
            <CollapsedRateList id={morePackagesId}>
              {collapsedRates.map((rate, index) => (
                <ChatHotelRoomOptionsRateOption
                  key={`${rate.packageId ?? rate.packageName ?? 'package'}-${rate.roomRateId ?? rate.rateName ?? `rate-${index}`}`}
                  rate={rate}
                  {...rateRowProps}
                  isRoomOptionSelected={isRateSelected(rate, selections)}
                />
              ))}
            </CollapsedRateList>
          )}
        </>
      )}
    </RoomCard>
  )
}

export default ChatHotelRoomOptionsRoomCard
